import React, { useRef, useState } from 'react';
import {
  Upload,
  Button,
  Image,
  Select,
  Space,
  List,
  Tag,
  Popconfirm,
  Typography,
  Progress,
} from 'antd';
import {
  UploadOutlined,
  CameraOutlined,
  DeleteOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import type { InspectionAttachment } from '../../services/qc-workflow.service';
import {
  useInspectionAttachments,
  useUploadInspectionAttachment,
  useDeleteInspectionAttachment,
} from '../../hooks/useQCWorkflow';

const ATTACHMENT_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'dimension_evidence', label: 'Dimension Evidence' },
  { value: 'visual_inspection', label: 'Visual Inspection' },
  { value: 'material_evidence', label: 'Material Evidence' },
  { value: 'ncr_evidence', label: 'NCR Evidence' },
  { value: 'customer_witness', label: 'Customer / Witness' },
  { value: 'photo', label: 'Photo' },
];

const CATEGORY_COLORS: Record<string, string> = {
  general: 'default',
  dimension_evidence: 'blue',
  visual_inspection: 'green',
  material_evidence: 'purple',
  ncr_evidence: 'red',
  customer_witness: 'orange',
  photo: 'cyan',
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

interface InspectionAttachmentsProps {
  inspectionId: string;
  readOnly?: boolean;
}

const getFileIcon = (fileType: string | null | undefined) => {
  if (!fileType) return <FileOutlined />;
  if (fileType.startsWith('image/')) return <FileImageOutlined />;
  if (fileType === 'application/pdf') return <FilePdfOutlined />;
  return <FileOutlined />;
};

const isImage = (fileType: string | null | undefined) =>
  fileType?.startsWith('image/');

const formatFileSize = (bytes: number | null | undefined) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const InspectionAttachments: React.FC<InspectionAttachmentsProps> = ({
  inspectionId,
  readOnly = false,
}) => {
  const { data: attachments = [], isLoading } = useInspectionAttachments(inspectionId);
  const uploadMutation = useUploadInspectionAttachment();
  const deleteMutation = useDeleteInspectionAttachment();

  const [selectedCategory, setSelectedCategory] = useState('general');
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      return;
    }
    await uploadMutation.mutateAsync({
      inspectionId,
      file,
      category: selectedCategory,
    });
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  const imageAttachments = attachments.filter((a: InspectionAttachment) => isImage(a.file_type));
  const nonImageAttachments = attachments.filter((a: InspectionAttachment) => !isImage(a.file_type));

  const baseUrl = (window as any).__API_BASE_URL || '';
  const getDownloadUrl = (att: InspectionAttachment) =>
    `${baseUrl}/uploads/${att.file_path}`;

  return (
    <div>
      {!readOnly && (
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Select
              value={selectedCategory}
              onChange={setSelectedCategory}
              style={{ width: 200 }}
              options={ATTACHMENT_CATEGORIES}
              placeholder="Category"
            />
            <Upload
              showUploadList={false}
              beforeUpload={(file) => {
                handleUpload(file);
                return false;
              }}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              multiple={false}
            >
              <Button icon={<UploadOutlined />} loading={uploadMutation.isPending}>
                Upload File
              </Button>
            </Upload>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleCameraCapture}
            />
            <Button
              icon={<CameraOutlined />}
              onClick={() => cameraInputRef.current?.click()}
              loading={uploadMutation.isPending}
            >
              Take Photo
            </Button>
          </Space>
          {uploadMutation.isPending && (
            <Progress percent={99} status="active" size="small" style={{ marginTop: 8 }} />
          )}
        </div>
      )}

      {/* Image gallery */}
      {imageAttachments.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Photos ({imageAttachments.length})
          </Typography.Text>
          <Image.PreviewGroup>
            <Space wrap size={8}>
              {imageAttachments.map((att: InspectionAttachment) => (
                <div key={att.id} style={{ position: 'relative', display: 'inline-block' }}>
                  <Image
                    src={getDownloadUrl(att)}
                    alt={att.file_name}
                    width={100}
                    height={100}
                    style={{ objectFit: 'cover', borderRadius: 4 }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQI12NgAAIABQABNjN9GQAAAABJRUEFAAUYAAAAEgAAACJJREFUCNdj+P//PwMACPwC/2VGYAAAAABJRU5ErkJggg=="
                  />
                  <div style={{ textAlign: 'center', fontSize: 11 }}>
                    <Tag color={CATEGORY_COLORS[att.category]} style={{ fontSize: 10 }}>{att.category}</Tag>
                  </div>
                  {!readOnly && (
                    <Popconfirm
                      title="Remove this photo?"
                      onConfirm={() => deleteMutation.mutate({ attachmentId: att.id, inspectionId })}
                    >
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(255,255,255,0.8)' }}
                      />
                    </Popconfirm>
                  )}
                </div>
              ))}
            </Space>
          </Image.PreviewGroup>
        </div>
      )}

      {/* Non-image files list */}
      {nonImageAttachments.length > 0 && (
        <div>
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Documents ({nonImageAttachments.length})
          </Typography.Text>
          <List
            size="small"
            loading={isLoading}
            dataSource={nonImageAttachments}
            renderItem={(att: InspectionAttachment) => (
              <List.Item
                actions={[
                  <a
                    key="download"
                    href={getDownloadUrl(att)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <DownloadOutlined />
                  </a>,
                  ...(!readOnly
                    ? [
                        <Popconfirm
                          key="delete"
                          title="Remove this file?"
                          onConfirm={() => deleteMutation.mutate({ attachmentId: att.id, inspectionId })}
                        >
                          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                        </Popconfirm>,
                      ]
                    : []),
                ]}
              >
                <List.Item.Meta
                  avatar={getFileIcon(att.file_type)}
                  title={att.file_name}
                  description={
                    <Space size={4}>
                      {formatFileSize(att.file_size)}
                      <Tag color={CATEGORY_COLORS[att.category]} style={{ fontSize: 10 }}>{att.category}</Tag>
                      {att.uploader?.name && <span>by {att.uploader.name}</span>}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}

      {attachments.length === 0 && !isLoading && (
        <Typography.Text type="secondary">
          No attachments yet{!readOnly ? ' — upload files or take photos above' : ''}
        </Typography.Text>
      )}
    </div>
  );
};

export default InspectionAttachments;
